import type { Household } from "../services/householdService";
import { getUserHousehold } from "../services/householdService";
import { useQuery } from "./useDataCache";
import { useUser } from "./useUser";

// Module-level cache of the last successfully fetched household record.
// Lives outside React so it is never affected by render cycles, effect timing,
// or React Query error states. Only written inside queryFn (not during render)
// on a confirmed successful fetch, and cleared explicitly on sign-out.
let _lastKnownHousehold: Household | null = null;

export function clearLastKnownHousehold() {
  _lastKnownHousehold = null;
}

// Hook to load the current household record.
//
// Design contract — mirrors useUser's guarantee:
//   - Once a household is successfully fetched it is held in a module-level
//     cache and NEVER cleared by a failed or in-flight background re-fetch.
//   - isLoading is only true when there is genuinely no household data at all.
//   - isFetching is exposed so callers can show a subtle background-refresh
//     indicator without hiding the existing household UI.
export function useHousehold() {
  const {
    householdId,
    isLoading: isLoadingHouseholdId,
    isFetching: isFetchingHouseholdId,
  } = useUser();

  const {
    data: fetchedHousehold,
    isFetching: isFetchingHousehold,
    ...queryInfo
  } = useQuery({
    queryKey: ["household", householdId],
    queryFn: async () => {
      const result = await getUserHousehold(householdId ?? undefined);
      // Write to the module-level cache on every successful fetch.
      // queryFn runs outside of render so this is safe.
      if (result) _lastKnownHousehold = result;
      return result;
    },
    enabled: !!householdId,
  });

  // The authoritative household: fetched value first, then the module-level
  // last-known-good cache, then null only if we have truly never had a household.
  const household = fetchedHousehold ?? _lastKnownHousehold;

  // isLoading is true when:
  //   - we have no household ID yet (and it's being fetched for the first time)
  //   - we have a household ID but no household record anywhere yet
  // It is NOT true during background re-fetches when we already have data.
  const isLoading =
    isLoadingHouseholdId ||
    isFetchingHouseholdId ||
    (!!householdId && queryInfo.isLoading && !household);

  return {
    household,
    isFetching: isFetchingHouseholdId || isFetchingHousehold,
    ...queryInfo,
    isLoading,
  };
}
